import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Target, RefreshCw, Download, Loader2, ChevronRight, Building2, Landmark,
  Pencil, Check, X, User, ClipboardEdit, ArrowLeft, Coins, Cpu, UserCog, Banknote,
} from "lucide-react";
// Fix WH57 (2026-09-23): metas de venta por sucursal y mes (Venta vs meta).
import MetasVentaModal from "@/components/ops/MetasVentaModal";
// Fix WH61 (2026-09-25): dashboard general (hoja Dashboard del Excel de Gabriela).
import KpiDashboardGeneral from "@/components/ops/KpiDashboardGeneral";

const SEMAFORO_BG = {
  verde: "bg-green-500",
  amarillo: "bg-amber-400",
  rojo: "bg-red-500",
};
const SEMAFORO_LABEL = { verde: "Verde", amarillo: "Amarillo", rojo: "Rojo" };
const FREQ_LABEL = { diaria: "Diaria", semanal: "Semanal", mensual: "Mensual" };
// Fix WH61 (2026-09-25): mismos roles que BOARD_ADMIN_ROLES y CAPTURE_ROLES de
// backend/routes/kpi_board.py. En la vista dashboard no se carga el tablero por
// sucursal, así que los botones del encabezado no pueden depender de data.
const ROLES_ADMIN_KPIS = ["corporate_admin", "direccion", "hr_admin"];
const ROLES_CAPTURA_KPIS = ["corporate_admin", "hr_admin"];

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtVal(v, unit) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (unit === "%") return `${n.toLocaleString("es-MX", { maximumFractionDigits: 2 })}%`;
  if (unit === "MXN") return `$${n.toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
  return n.toLocaleString("es-MX", { maximumFractionDigits: 2 });
}

function Semaforo({ level, size = "md" }) {
  const dim = size === "lg" ? "w-4 h-4" : "w-2.5 h-2.5";
  if (!level) {
    return <span className={`inline-block ${dim} rounded-full bg-slate-200 ring-2 ring-slate-100`} title="Sin dato" data-testid="semaforo-sin-dato" />;
  }
  return <span className={`inline-block ${dim} rounded-full ${SEMAFORO_BG[level]}`} title={SEMAFORO_LABEL[level]} data-testid={`semaforo-${level}`} />;
}

function ScoreBadge({ score }) {
  if (score === null || score === undefined) return <span className="text-sm text-slate-400 italic">sin dato</span>;
  // Fix WH57 (2026-09-23): 96, igual que el semaforo del backend (WH56) y el bono.
  const cls = score >= 96 ? "text-green-600" : score >= 90 ? "text-amber-500" : "text-red-500";
  return <span className={`text-2xl font-extrabold ${cls}`}>{score.toLocaleString("es-MX", { maximumFractionDigits: 1 })}%</span>;
}

function SourceTag({ source }) {
  if (source === "auto") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        <Cpu className="w-3 h-3" /> Automático
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
      <UserCog className="w-3 h-3" /> Captura RH
    </span>
  );
}

// ── Responsable editable inline ──────────────────────────────
function ResponsibleInline({ responsible, canEdit, categoryCode, branchId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(responsible?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(responsible?.name || ""); }, [responsible]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/ops/kpis/categories/${categoryCode}/responsible`, {
        name: draft.trim() || null,
        branch_id: branchId || null,
      });
      toast.success("Responsable actualizado");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el responsable");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Nombre del responsable"
          className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
          data-testid="responsible-input"
        />
        <button onClick={save} disabled={saving} className="text-green-600 hover:text-green-700" data-testid="responsible-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600" data-testid="responsible-cancel-btn">
          <X className="w-4 h-4" />
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-700" data-testid="responsible-display">
      <User className="w-3.5 h-3.5 text-slate-400" />
      {responsible?.name || <span className="text-slate-400 italic">Sin responsable asignado</span>}
      {canEdit && (
        <button onClick={() => setEditing(true)} className="text-slate-300 hover:text-slate-500" title="Editar responsable" data-testid="responsible-edit-btn">
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

// ── Página principal ─────────────────────────────────────────
export default function OpsKpiBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(currentPeriod());
  const [weekMode, setWeekMode] = useState(false);
  const [weekDate, setWeekDate] = useState("");
  const [nav, setNav] = useState({ branchId: null, branchName: null, categoryCode: null, categoryName: null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  // Fix WH57 (2026-09-23): modal de metas de venta.
  const [showMetas, setShowMetas] = useState(false);
  // Fix WH61 (2026-09-25): Corporativo, Dirección y RH entran al dashboard
  // general; los demás roles siguen en el tablero por sucursal, como hoy.
  const esAdminKpis = ROLES_ADMIN_KPIS.includes(user?.role);
  const puedeCapturar = ROLES_CAPTURA_KPIS.includes(user?.role);
  const [vista, setVista] = useState(esAdminKpis ? "dashboard" : "tablero");
  // Sube al recalcular automáticos o guardar metas: el dashboard se vuelve a pedir.
  const [recarga, setRecarga] = useState(0);

  const isRestrictedCorp = user?.role === "treasury" || user?.role === "legal";

  const load = useCallback(async () => {
    // Fix WH61 (2026-09-25): en la vista dashboard no se pide el tablero por
    // sucursal (tarda ~7.5 s en producción y no se muestra).
    if (vista === "dashboard") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (weekMode && weekDate) params.set("week_start", weekDate);
      if (nav.branchId) params.set("branch_id", nav.branchId);
      if (nav.categoryCode) params.set("category_code", nav.categoryCode);
      const res = await api.get(`/ops/kpis/board?${params}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el Tablero de KPIs");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, weekMode, weekDate, nav, vista]);

  useEffect(() => { load(); }, [load]);

  // treasury/legal entran directo al corporativo
  useEffect(() => {
    if (isRestrictedCorp && !nav.branchId) {
      setNav({ branchId: "corporativo", branchName: "Corporativo", categoryCode: null, categoryName: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestrictedCorp]);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await api.post(`/ops/kpis/board/recompute?period=${period}`);
      toast.success(`Recalculados ${res.data.computed?.length || 0} KPIs automáticos`);
      load();
      setRecarga((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo recalcular");
    } finally {
      setRecomputing(false);
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams({ period });
    if (weekMode && weekDate) params.set("week_start", weekDate);
    api.get(`/ops/kpis/board/export.xlsx?${params}`, { responseType: "blob" })
      .then((res) => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `tablero_kpis_${period}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => toast.error("No se pudo exportar el Excel"));
  };

  const goHome = () => setNav({ branchId: null, branchName: null, categoryCode: null, categoryName: null });
  // Fix WH61 (2026-09-25): loading en true antes de cambiar de vista, para que no
  // se vea un instante el tablero anterior mientras carga.
  const verTablero = () => {
    if (vista === "tablero") return;
    setLoading(true);
    setVista("tablero");
  };
  const abrirAreaDesdeDashboard = (code, name, branchId, branchName) => {
    setLoading(true);
    setNav({ branchId, branchName, categoryCode: code, categoryName: name });
    setVista("tablero");
  };
  const goBranch = () => setNav((p) => ({ ...p, categoryCode: null, categoryName: null }));

  const level = data?.level;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6" data-testid="kpi-board-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Target className="w-6 h-6 text-[#1A1A1A]" />
            <h1 className="text-2xl font-bold text-slate-900">Tablero de KPIs</h1>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Semáforo por sucursal → puesto → persona. Verde ≥96%, amarillo ≥90%, rojo &lt;90%.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {esAdminKpis && (
            <button
              onClick={() => setShowMetas(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
              data-testid="board-metas-venta-btn"
            >
              <Banknote className="w-4 h-4" /> Metas de venta
            </button>
          )}
          {puedeCapturar && (
            <button
              onClick={() => navigate("/ops/kpis/captura")}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
              data-testid="board-captura-link"
            >
              <ClipboardEdit className="w-4 h-4" /> Captura RH
            </button>
          )}
          {esAdminKpis && (
            <button
              onClick={handleRecompute}
              disabled={recomputing}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              data-testid="board-recompute-btn"
            >
              {recomputing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Recalcular automáticos
            </button>
          )}
          {esAdminKpis && (
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-[#1A1A1A] bg-[#FFDF00] rounded-lg hover:brightness-95"
              data-testid="board-export-btn"
            >
              <Download className="w-4 h-4" /> Exportar Excel
            </button>
          )}
        </div>
      </div>

      {/* Filtros de período */}
      <div className="flex flex-wrap items-center gap-3 bg-slate-50 rounded-xl p-3">
        {/* Fix WH61 (2026-09-25): selector de vista, solo para roles admin de KPIs. */}
        {esAdminKpis && (
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
            <button
              onClick={() => setVista("dashboard")}
              className={`px-3 py-1.5 font-medium ${vista === "dashboard" ? "bg-[#FFDF00] text-[#1A1A1A]" : "bg-white text-slate-600"}`}
              data-testid="board-vista-dashboard"
            >
              Dashboard general
            </button>
            <button
              onClick={verTablero}
              className={`px-3 py-1.5 font-medium ${vista === "tablero" ? "bg-[#FFDF00] text-[#1A1A1A]" : "bg-white text-slate-600"}`}
              data-testid="board-vista-tablero"
            >
              Por sucursal
            </button>
          </div>
        )}
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
          <button
            onClick={() => setWeekMode(false)}
            className={`px-3 py-1.5 font-medium ${!weekMode ? "bg-[#1A1A1A] text-white" : "bg-white text-slate-600"}`}
            data-testid="board-mode-mes"
          >
            Mes
          </button>
          <button
            onClick={() => setWeekMode(true)}
            className={`px-3 py-1.5 font-medium ${weekMode ? "bg-[#1A1A1A] text-white" : "bg-white text-slate-600"}`}
            data-testid="board-mode-semana"
          >
            Semana
          </button>
        </div>
        <input
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
          data-testid="board-period-input"
        />
        {weekMode && (
          <input
            type="date"
            value={weekDate}
            onChange={(e) => setWeekDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
            title="Cualquier día de la semana a consultar"
            data-testid="board-week-input"
          />
        )}
        {vista === "tablero" && data?.window && (
          <span className="text-xs text-slate-400">Ventana: {data.window.from} → {data.window.to}</span>
        )}
      </div>

      {/* Breadcrumb */}
      {vista === "tablero" && (nav.branchId || nav.categoryCode) && (
        <div className="flex items-center gap-1.5 text-sm flex-wrap" data-testid="board-breadcrumb">
          <button onClick={goHome} className="text-slate-500 hover:text-slate-800 font-medium inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Sucursales
          </button>
          {nav.branchName && (
            <>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              <button onClick={goBranch} className={`font-medium ${nav.categoryCode ? "text-slate-500 hover:text-slate-800" : "text-slate-900"}`}>
                {nav.branchName}
              </button>
            </>
          )}
          {nav.categoryName && (
            <>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              <span className="font-semibold text-slate-900">{nav.categoryName}</span>
            </>
          )}
        </div>
      )}

      {vista === "dashboard" ? (
        <KpiDashboardGeneral
          period={period}
          weekStart={weekMode && weekDate ? weekDate : null}
          recarga={recarga}
          onAbrirArea={abrirAreaDesdeDashboard}
        />
      ) : loading || !data ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="bg-white rounded-xl border border-slate-200 h-32 animate-pulse" />)}
        </div>
      ) : level === "sucursal" ? (
        <BoardLevel1
          data={data}
          onOpenBranch={(id, name) => setNav({ branchId: id, branchName: name, categoryCode: null, categoryName: null })}
        />
      ) : level === "area" ? (
        <BoardLevel2
          data={data}
          onOpenPuesto={(code, name) => setNav((p) => ({ ...p, categoryCode: code, categoryName: name }))}
        />
      ) : level === "puesto" ? (
        <BoardLevel3 data={data} onReload={load} branchIdForResponsible={data.scope === "por_sucursal" ? data.branch_id : null} />
      ) : null}
      {showMetas && (
        <MetasVentaModal
          period={period}
          onClose={() => setShowMetas(false)}
          onSaved={() => {
            load();
            setRecarga((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

// ── NIVEL 1 — Sucursales + Corporativo ───────────────────────
function BoardLevel1({ data, onOpenBranch }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="board-level-1">
      {(data.branches || []).map((b) => (
        <button
          key={b.branch_id}
          onClick={() => onOpenBranch(b.branch_id, b.name)}
          className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#FFDF00] hover:shadow-sm transition-all"
          data-testid={`board-branch-card-${b.branch_id}`}
        >
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-800">
              <Building2 className="w-4 h-4 text-slate-400" /> {b.name}
            </span>
            <Semaforo level={b.semaforo} size="lg" />
          </div>
          <div className="mt-3"><ScoreBadge score={b.score_pct} /></div>
          <div className="text-xs text-slate-400 mt-1.5">
            {b.puestos_with_data}/{b.puestos_total} puestos con datos
          </div>
          {b.responsible?.name && (
            <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
              <User className="w-3 h-3" /> {b.responsible.name}
            </div>
          )}
        </button>
      ))}
      {data.corporate && (
        <button
          onClick={() => onOpenBranch("corporativo", "Corporativo")}
          className="text-left bg-[#1A1A1A] text-white rounded-2xl p-5 hover:ring-2 hover:ring-[#FFDF00] transition-all"
          data-testid="board-corporate-card"
        >
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-sm font-bold">
              <Landmark className="w-4 h-4 text-white/60" /> Corporativo
            </span>
            <Semaforo level={data.corporate.semaforo} size="lg" />
          </div>
          <div className="mt-3">
            {data.corporate.score_pct != null ? (
              <span className="text-2xl font-extrabold text-[#FFDF00]">
                {data.corporate.score_pct.toLocaleString("es-MX", { maximumFractionDigits: 1 })}%
              </span>
            ) : (
              <span className="text-sm text-white/50 italic">sin dato</span>
            )}
          </div>
          <div className="text-xs text-white/40 mt-1.5">
            {data.corporate.puestos_with_data}/{data.corporate.puestos_total} puestos con datos
          </div>
        </button>
      )}
    </div>
  );
}

// ── NIVEL 2 — Puestos/áreas de la sucursal o del corporativo ─
function BoardLevel2({ data, onOpenPuesto }) {
  return (
    <div className="space-y-3" data-testid="board-level-2">
      <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
        Puestos de {data.branch_name}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {(data.puestos || []).map((p) => (
          <button
            key={p.category_code}
            onClick={() => onOpenPuesto(p.category_code, p.category_name)}
            className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#FFDF00] hover:shadow-sm transition-all"
            data-testid={`board-puesto-card-${p.category_code}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold text-slate-800">{p.category_name}</span>
              <Semaforo level={p.semaforo} size="lg" />
            </div>
            <div className="mt-2 flex items-end justify-between gap-2">
              <ScoreBadge score={p.total_score} />
              {p.bonus && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  <Coins className="w-3 h-3" /> {p.bonus.label}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-1.5">
              {p.captured_count}/{p.kpi_count} KPIs con datos
            </div>
            <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
              <User className="w-3 h-3" />
              {p.responsible?.name || <span className="italic text-slate-400">Sin responsable</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── NIVEL 3 — Detalle del puesto (objetivo + responsable + KPIs) ─
function BoardLevel3({ data, onReload, branchIdForResponsible }) {
  return (
    <div className="space-y-4" data-testid="board-level-3">
      {/* Cabecera del puesto */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-slate-900">
            {data.category_name}
            {data.branch_name && <span className="text-slate-400 font-medium"> · {data.branch_name}</span>}
          </h2>
          <ResponsibleInline
            responsible={data.responsible}
            canEdit={data.can_admin}
            categoryCode={data.category_code}
            branchId={branchIdForResponsible}
            onSaved={onReload}
          />
        </div>
        {data.objective ? (
          <p className="text-sm text-slate-600" data-testid="puesto-objective">
            <span className="font-semibold text-slate-700">Objetivo:</span> {data.objective}
          </p>
        ) : (
          <p className="text-sm text-slate-400 italic">Sin objetivo configurado.</p>
        )}
      </div>

      {/* Tabla de KPIs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                <th className="py-2 px-3">KPI</th>
                <th className="py-2 px-3">Qué mide</th>
                <th className="py-2 px-3">Meta</th>
                <th className="py-2 px-3">Valor actual</th>
                <th className="py-2 px-3">Semáforo</th>
                <th className="py-2 px-3">Frecuencia</th>
                <th className="py-2 px-3">Ponderación</th>
                <th className="py-2 px-3">Puntos</th>
              </tr>
            </thead>
            <tbody>
              {(data.kpis || []).map((k) => (
                <tr key={k.kpi_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`board-kpi-row-${k.code}`}>
                  <td className="py-2.5 px-3">
                    <div className="text-sm font-medium text-slate-800">{k.name}</div>
                    <div className="mt-0.5"><SourceTag source={k.source} /></div>
                  </td>
                  <td className="py-2.5 px-3 text-xs text-slate-500 max-w-[220px]">{k.measures || "—"}</td>
                  <td className="py-2.5 px-3 text-sm text-slate-700">{k.target_display || fmtVal(k.target, k.unit)}</td>
                  <td className="py-2.5 px-3">
                    {k.captured ? (
                      <span className="text-sm font-semibold text-slate-800">{fmtVal(k.value, k.unit)}</span>
                    ) : (
                      <span className="text-xs text-slate-400 italic" data-testid={`board-kpi-sindato-${k.code}`}>sin dato</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3"><Semaforo level={k.semaforo} /></td>
                  <td className="py-2.5 px-3 text-xs text-slate-500">{FREQ_LABEL[k.frequency] || k.frequency}</td>
                  <td className="py-2.5 px-3 text-xs text-slate-500">{k.weight_pct}%</td>
                  <td className="py-2.5 px-3 text-sm font-semibold text-slate-700">{k.points}</td>
                </tr>
              ))}
              {(!data.kpis || data.kpis.length === 0) && (
                <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-400">Este puesto no tiene KPIs configurados</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Resultado total + bono */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-3" data-testid="puesto-total-row">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-slate-700">Resultado total del puesto:</span>
            <ScoreBadge score={data.total_score} />
            <Semaforo level={data.semaforo} size="lg" />
          </div>
          <div className="flex items-center gap-2">
            <Coins className="w-4 h-4 text-amber-500" />
            {data.bonus ? (
              <span className="text-sm font-semibold text-slate-700" data-testid="puesto-bonus-label">
                Bono: {data.bonus.label}
              </span>
            ) : (
              <span className="text-sm text-slate-400 italic" data-testid="puesto-bonus-label">Bono: sin datos del período</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
